import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

// Charcoal + Teal palette (active)
const C = {
  primary:    '#1C2B2B',
  secondary:  '#4897a0',
  bg:         '#F7FAFA',
  successBg:  '#F0F7F7',
};

export default function ReinvitePage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | valid | already_confirmed | expired | error | confirmed
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reinvite/${token}`)
      .then(res => {
        if (res.data.status === 'already_confirmed') {
          setState('already_confirmed');
        } else {
          setData(res.data);
          setState('valid');
        }
      })
      .catch(err => {
        if (err.response?.status === 410) setState('expired');
        else setState('error');
      });
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/reinvite/${token}/confirm`);
      setState('confirmed');
    } catch (err) {
      if (err.response?.data?.status === 'already_confirmed') {
        setState('already_confirmed');
      } else {
        setState('error');
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', fontFamily: 'Georgia, serif' }}>
      {/* Card */}
      <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', maxWidth: 520, width: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: C.primary, padding: '24px 32px', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 'bold', letterSpacing: 0.5 }}>FB Attorneys Academy</p>
          <p style={{ margin: '4px 0 0', color: C.secondary, fontSize: 13 }}>Legal &amp; Tech Hub</p>
        </div>

        {/* Body */}
        <div style={{ padding: '36px 32px' }}>
          {state === 'loading' && (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 15, padding: '24px 0' }}>
              Loading your invitation...
            </div>
          )}

          {state === 'valid' && data && (
            <>
              <p style={{ color: C.primary, fontSize: 16, margin: '0 0 8px', fontWeight: 'bold' }}>
                Dear {data.applicant_name},
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                You have been invited to express your interest in joining <strong>{data.intake_name}</strong> at FB Attorneys Academy.
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: '0 0 28px' }}>
                Clicking the button below will register your interest. Please note that this does not guarantee a place — shortlisted candidates will be contacted with further instructions.
              </p>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  display: 'block', width: '100%', padding: '14px 24px',
                  background: confirming ? '#6b7280' : C.secondary, color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 'bold',
                  cursor: confirming ? 'not-allowed' : 'pointer', fontFamily: 'Arial, sans-serif',
                }}
              >
                {confirming ? 'Registering...' : 'Confirm My Interest'}
              </button>
            </>
          )}

          {state === 'confirmed' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.successBg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 28, color: C.secondary }}>✓</span>
                </div>
              </div>
              <p style={{ color: C.secondary, fontSize: 17, fontWeight: 'bold', textAlign: 'center', margin: '0 0 12px' }}>
                Interest Registered
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, textAlign: 'center', margin: 0 }}>
                Thank you. Your interest has been registered successfully. Our team will review your profile and contact you with further instructions if you are shortlisted.
              </p>
            </>
          )}

          {state === 'already_confirmed' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.successBg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 28, color: C.secondary }}>✓</span>
                </div>
              </div>
              <p style={{ color: C.secondary, fontSize: 17, fontWeight: 'bold', textAlign: 'center', margin: '0 0 12px' }}>
                Already Registered
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, textAlign: 'center', margin: 0 }}>
                Your interest has already been registered. Our team will be in touch if you are shortlisted.
              </p>
            </>
          )}

          {state === 'expired' && (
            <>
              <p style={{ color: C.primary, fontSize: 16, fontWeight: 'bold', margin: '0 0 12px' }}>
                Invitation Expired
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                This invitation link has expired. Please contact FB Attorneys Academy if you believe this is an error.
              </p>
            </>
          )}

          {state === 'error' && (
            <>
              <p style={{ color: C.primary, fontSize: 16, fontWeight: 'bold', margin: '0 0 12px' }}>
                Invalid Link
              </p>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                This invitation link is invalid or has already been used. Please contact FB Attorneys Academy for assistance.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ background: C.primary, padding: '16px 32px', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: 12 }}>8th Floor, Amani Place, Ohio Street, Dar es Salaam</p>
        </div>
      </div>
    </div>
  );
}
